/**
 * Attendu 621 — Comprendre et connaître la définition du quotient
 * d'un entier a par un entier b non nul.
 *
 * 8 générateurs : QCM / saisie / SVG droite graduée / V-F / relier / texte-trou.
 */

import {
    randInt,
    randomChoice,
    shuffle,
    makeQcm,
    makeSaisie,
    makeVraiFaux,
    makeRelier,
    makeTexteTrou,
    filtrerDistracteurs,
} from '../../../utils';

import {
    mkFrac,
    gcd,
    renderFrac,
} from './fraction.js';

const ATTENDU = 621;

const exprElement = (katexStr) => ({
    version: 1,
    elements: [{ type: 'expression', config: { contenu: katexStr } }],
});


/* ──────────────────────────────────────────────────────────────
 * 621-T1 — QCM : « par quel nombre faut-il multiplier a pour obtenir b ? »
 *   Réponse : b/a
 * ────────────────────────────────────────────────────────────── */
export const genDefQuotientQcm = () => {
    let a = randInt(2, 15);
    let b = randInt(2, 30);
    if (a === b) b += 1;
    const correct = `$${renderFrac(mkFrac(b, a))}$`;
    // Distracteurs dédupliqués (b-a et b/a peuvent coïncider quand b=2a, etc.)
    const distractorsRaw = [
        `$${renderFrac(mkFrac(a, b))}$`,
        // Erreur additive : « a + ? = b » ou « a − ? = b » selon le sens —
        // dans les deux cas l'élève produit l'ÉCART (l'ancien b − a affichait
        // « −4 » dans 21 % des tirages, revue 2026-08-01)
        `$${Math.abs(b - a)}$`,
        `$${b + a}$`,
        `$${b * a}$`,
    ];
    const distractors = [...new Set(distractorsRaw)].filter(d => d !== correct).slice(0, 3);
    // Formulation calée sur l'item officiel du programme (recentrage 2026-08-01)
    const texte = `<p>Quel nombre, multiplié par <strong>${a}</strong>, donne <strong>${b}</strong> ?</p>`;
    return makeQcm([correct, ...distractors], correct, texte, {
        niveau: '6eme', matiere: 'maths', attendu: ATTENDU,
        label: "Trouver le nombre qui multiplié par a donne b",
    });
};

/* ──────────────────────────────────────────────────────────────
 * 621-T3 — Saisie : VÉRIFICATION de la définition — « 3 × 1/3 = … »
 * Recentrage 2026-08-01 : l'ancien « écris le quotient de a par b »
 * doublonnait l'attendu 620. L'item officiel de l'attendu 621 est le
 * calcul b × a/b — dont la réponse est l'ENTIER a : c'est le geste de
 * vérification qui fonde la caractérisation du quotient.
 * ────────────────────────────────────────────────────────────── */
export const genDefQuotientTexte = () => {
    let a, b;
    do {
        a = randInt(2, 30);
        b = randInt(2, 12);
    } while (gcd(a, b) !== 1);
    const texte = `<p>Calcule :</p><p><strong>{{element:0}}</strong></p>`;
    return makeSaisie(String(a), texte, {
        niveau: '6eme', matiere: 'maths', attendu: ATTENDU,
        label: "Vérifier la définition : calculer b × a/b",
        strict: false,
        elements_additionnels: exprElement(`${b} \\times \\dfrac{${a}}{${b}} = \\;?`),
    });
};

/* ──────────────────────────────────────────────────────────────
 * 621-T4 — Saisie SVG : reporter b fois a/b — on tombe sur l'entier a
 * Refonte 2026-08-02 (idée utilisateur) : l'ancien format faisait LIRE
 * une fraction jamais entière — de la lecture de graduation, pas la
 * caractérisation. Ici on reporte b fois la longueur a/b et on tombe
 * SYSTÉMATIQUEMENT sur l'entier a : la droite montre b × a/b = a.
 * Saisie et non QCM : « le point posé sur un entier » serait sinon un
 * pattern gagnant sans calcul (défaut éliminable par habitude).
 * ────────────────────────────────────────────────────────────── */
// a/b irréductible et (a+1)·b ≤ 24 graduations (lisibilité) — 23 couples
// (le plafond 20 donnait 16 couples : sous le seuil U-VARIANTES du harnais)
const COUPLES_REPORTS = (() => {
    const res = [];
    for (let b = 2; b <= 10; b++)
        for (let a = 1; (a + 1) * b <= 24; a++)
            if (gcd(a, b) === 1) res.push([a, b]);
    return res;
})();

export const genDroiteGradueeSvg = () => {
    const [a, b] = randomChoice(COUPLES_REPORTS);
    // Un seul point marqué : le premier report, la fraction a/b — la
    // longueur à reporter rendue concrète sur la droite. Les cumuls
    // intermédiaires surchargeaient (retour utilisateur 2026-08-02).
    const points = [{
        value: a / b,
        label: '',
        color: '#f97316',
        showValue: true,
        displayFraction: { num: a, den: b },
    }];
    const reperes = [];
    for (let e = 1; e <= a; e++) reperes.push(e);
    const texte = `<p>À partir de 0, on reporte <strong>${b} fois</strong> la longueur {{element:1}} sur la demi-droite. Sur quel nombre entier tombe-t-on\u00a0?</p>{{element:0}}`;
    return makeSaisie(String(a), texte, {
        niveau: '6eme', matiere: 'maths', attendu: ATTENDU,
        label: "Reporter b fois la longueur a/b : tomber sur l'entier",
        strict: false,
        elements_additionnels: {
            version: 1,
            elements: [
                { type: 'numberline', config: { start: 0, end: a + 1, step: 1 / b, points, showStart: true, showEnd: true, reperes } },
                { type: 'expression', config: { contenu: renderFrac(mkFrac(a, b)) } },
            ],
        },
    });
};

/* ──────────────────────────────────────────────────────────────
 * 621-T5 — Tableau V/F : égalités b × (a/b) = a
 * ────────────────────────────────────────────────────────────── */
export const genDefQuotientVf = () => {
    const lignes = [];
    const bonnes = [];
    const wanted = shuffle([0, 0, 1, 1]); // 2 V, 2 F
    for (const etat of wanted) {
        let a, b;
        do {
            a = randInt(2, 20);
            b = randInt(2, 12);
            // copremiers (revue 2026-08-01) : couvre l'ancien garde a=b, et
            // évite les lignes dégénérées sans fraction (« 4 × 2 = 8 »)
        } while (gcd(a, b) !== 1);
        // Commutativité du programme (recentrage 2026-08-01) : la moitié des
        // lignes s'écrit dans l'ordre inverse a/b × b, vrai aussi
        const frac = `\\dfrac{${a}}{${b}}`;
        const gauche = Math.random() < 0.5 ? `${b} \\times ${frac}` : `${frac} \\times ${b}`;
        if (etat === 0) {
            lignes.push(`$${gauche} = ${a}$`);
        } else {
            // Faux : on remplace le résultat par b (piège classique)
            lignes.push(`$${gauche} = ${b}$`);
        }
        bonnes.push(etat);
    }
    const texte = `<p>Indique si chaque égalité est vraie ou fausse.</p>`;
    return makeVraiFaux(lignes, bonnes, texte, {
        niveau: '6eme', matiere: 'maths', attendu: ATTENDU,
        label: "Valider la définition du quotient",
    });
};

/* ──────────────────────────────────────────────────────────────
 * 621-T6 — Relier énoncé ↔ fraction (flash définition)
 * ────────────────────────────────────────────────────────────── */
export const genDefQuotientRelier = () => {
    // Refonte anti « matching de chiffres » (idée utilisateur 2026-08-02) :
    // avec 4 paires aux nombres différents, l'élève appariait « la fraction
    // qui contient 23 et 7 » sans traiter l'ORDRE. Désormais : deux énoncés
    // CROISÉS sur le même couple — chaque bonne réponse est le piège de
    // l'autre — plus deux leurres entiers reprenant les erreurs du QCM
    // (produit, somme). Par construction : fractions distinctes (copremiers,
    // a≠b), jamais égales aux entiers (b/a non entier), produit ≠ somme
    // (l'égalité exigerait a=b=2, exclu par copremiers).
    let a, b;
    do {
        a = randInt(3, 25);
        b = randInt(2, 15);
    } while (gcd(a, b) !== 1);
    const pairs = [
        { source: `Le nombre qui, multiplié par ${a}, donne ${b}`, target: `$\\dfrac{${b}}{${a}}$` },
        { source: `Le nombre qui, multiplié par ${b}, donne ${a}`, target: `$\\dfrac{${a}}{${b}}$` },
    ];
    const leurres = [
        { target: `$${a * b}$`, code: 'produit' },
        { target: `$${a + b}$`, code: 'somme' },
    ];
    const texte = `<p>Relie chaque énoncé au nombre qui lui correspond. `
        + `Attention : certaines cibles ne correspondent à aucun énoncé.</p>`;
    const payload = makeRelier(pairs, texte, {
        niveau: '6eme', matiere: 'maths', attendu: ATTENDU,
        label: "Flash — trouver la fraction (définition)",
        cibles_leurres: leurres,
    });
    payload._meta.params = {
        a, b,
        distracteurs: { [`${a * b}`]: 'produit', [`${a + b}`]: 'somme' },
    };
    return payload;
};

/* ──────────────────────────────────────────────────────────────
 * 621-T7 — Drag : la PHRASE de la définition à compléter en mots
 * Recentrage 2026-08-01 : l'ancien « Par définition, b × {{a/b}} = a »
 * était un trou multiplicatif symbolique — le territoire de 622. Ici on
 * part de la fraction et on fait écrire la phrase : le miroir de T8.
 * Pièges : l'opération (multiplié / divisé / ajouté) et l'ORDRE (par b,
 * donne a) — l'inversion est comptée fausse positionnellement, voulu.
 * ────────────────────────────────────────────────────────────── */
export const genDefQuotientDrag = () => {
    let a, b;
    do {
        a = randInt(3, 20);
        b = randInt(2, 12);
    } while (gcd(a, b) !== 1);
    // Deux lignes math (fix mobile 2026-08-02) : KaTeX display ne se coupe
    // jamais seul — la coupure est choisie ici, sémantique et fixe.
    const phrase = `$\\dfrac{${a}}{${b}} \\text{ est le nombre}$\n$\\text{qui, } {{multiplié}} \\text{ par } {{${b}}} \\text{, donne } {{${a}}}.$`;
    const texte = `<p>Complète la définition avec les bonnes étiquettes.</p>`;
    const dist = filtrerDistracteurs(phrase, [
        'divisé',
        'ajouté',
    ]);
    return makeTexteTrou(phrase, texte, {
        niveau: '6eme', matiere: 'maths', attendu: ATTENDU,
        label: "Verbaliser la définition du quotient",
        mode: 'drag',
        distracteurs: dist,
    });
};

/* ──────────────────────────────────────────────────────────────
 * 621-T8 — Texte à trous saisie : num / dén de la définition
 * ────────────────────────────────────────────────────────────── */
export const genDefQuotientTrouSaisie = () => {
    // Copremiers (revue 2026-08-01) : l'élève qui saisissait la fraction
    // simplifiée (même nombre) était rejeté dans 39 % des tirages ; strict
    // retiré (doctrine tolérance numérique champ par champ).
    let a, b;
    do {
        a = randInt(3, 20);
        b = randInt(2, 15);
    } while (gcd(a, b) !== 1);
    // Deux lignes math (fix mobile 2026-08-02) : même découpe que le drag
    // Étais \rule (motif 652) : sans eux les champs recouvrent la barre
    const phrase = `$\\text{Le nombre qui, multiplié par } ${b} \\text{, donne } ${a} \\text{,}$\n$\\text{s'écrit } \\dfrac{{{${a}}}\\rule[-0.55em]{0pt}{0.55em}}{\\rule{0pt}{1.3em}{{${b}}}}.$`;
    const texte = `<p>Complète la phrase en saisissant le numérateur et le dénominateur.</p>`;
    return makeTexteTrou(phrase, texte, {
        niveau: '6eme', matiere: 'maths', attendu: ATTENDU,
        label: "Verbaliser la définition (saisie)",
        mode: 'saisie',
        strict: false,
    });
};

/* ──────────────────────────────────────────────────────────────
 * Registre
 * ────────────────────────────────────────────────────────────── */
export const GENERATORS = {
    def_quotient_qcm: genDefQuotientQcm,
    def_quotient_texte: genDefQuotientTexte,
    droite_graduee_svg: genDroiteGradueeSvg,
    def_quotient_vf: genDefQuotientVf,
    def_quotient_relier: genDefQuotientRelier,
    def_quotient_drag: genDefQuotientDrag,
    def_quotient_trou_saisie: genDefQuotientTrouSaisie,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', qcm_multiple: 'QCM Multi', texte: 'Saisie',
    texte_trou_saisie: 'Saisie trous', texte_trou_drag: 'Drag',
    relier: 'Relier', tableau_vf: 'Vrai/Faux', classer: 'Classer', ordonner: 'Ordonner',
    qcm: 'QCM', vf: 'Vrai/Faux', drag: 'Drag', trou_saisie: 'Saisie trous',
    svg: 'SVG',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    // Pas de calculatrice : définition et vérification se font de tête
    // (arbitrage utilisateur 2026-08-02, même logique que 620)
    payload.contenu = { ...payload.contenu, calculatrice: false };
    payload._meta.generatorKey = key;
    return payload;
};

export const CATEGORIES = [
    { id: 'def_quotient', label: 'Définition du quotient', formatsList: buildFormats('def_quotient') },
    { id: 'droite_graduee', label: 'Demi-droite graduée', formatsList: buildFormats('droite_graduee') },
];

const gen621 = { GENERATORS, generate, CATEGORIES };
export default gen621;
